import { useMemo } from "react";

import { useSoilSightCurrency, useTranslation } from "utils";

import { ContributionMarginYearBreakdown, GenerateSoilSightOutcomesResponse } from "models";

import { LiftClassification, classifyLift, getScenarioUpliftPercent } from "pages/app/scenario-analysis/utils/uplift";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { getCumulativeMarginTotal } from "pages/app/scenario-onboarding/utils/onboardingForecast";

export type OnboardingOutcomeSummary = {
  baselineYears: ContributionMarginYearBreakdown[];
  changeYears: ContributionMarginYearBreakdown[];
  upliftPct: number | null;
  liftClassification: LiftClassification;
  baselineTotal: number | null;
  changeTotal: number | null;
  formatMargin: (value: number) => string;
};

const extractYears = (outcomes: GenerateSoilSightOutcomesResponse | null, scenarioId: string | null): ContributionMarginYearBreakdown[] =>
  (scenarioId ? outcomes?.scenarios?.[scenarioId]?.years : undefined) ?? [];

/**
 * The one derivation of the outcome figures — per-scenario year series,
 * uplift (single figure + classification, from the api's avg band) and cumulative € totals —
 * so the two steps can never disagree on the numbers they present.
 */
export const useOnboardingOutcomeSummary = (): OnboardingOutcomeSummary => {
  const { t } = useTranslation(["scenarioAnalysis", "global"]);
  const { formatCmOutcome } = useSoilSightCurrency();
  const { scenarioRefs, outcomes } = useScenarioOnboarding();

  const baselineYears = useMemo(() => extractYears(outcomes, scenarioRefs.baselineScenarioId), [outcomes, scenarioRefs.baselineScenarioId]);
  const changeYears = useMemo(() => extractYears(outcomes, scenarioRefs.changeScenarioId), [outcomes, scenarioRefs.changeScenarioId]);

  const upliftPct = getScenarioUpliftPercent(outcomes, scenarioRefs.baselineScenarioId, scenarioRefs.changeScenarioId);
  const liftClassification = classifyLift(upliftPct);

  const baselineTotal = getCumulativeMarginTotal(baselineYears);
  const changeTotal = getCumulativeMarginTotal(changeYears);

  const formatMargin = (value: number) => t("scenarioAnalysis:onboarding.marginPerHa", { value: formatCmOutcome(value) });

  return { baselineYears, changeYears, upliftPct, liftClassification, baselineTotal, changeTotal, formatMargin };
};
